/**
 * JKL Promotional Banner Component
 * Migrated from: shopify-headless/app/components/collection/PromotionalBanner.tsx
 * 
 * Features:
 * - Swiper-based slider with autoplay
 * - Pagination dots
 * - Video support with auto-advance
 * - Theme Editor integration
 * - Loop enabled for seamless autoplay
 */

import Swiper from './swiper-bundle.esm.browser.min.js';

class JklPromotionalBanner extends HTMLElement {
  constructor() {
    super();
    
    // Instance properties
    this.swiper = null;
    this.blockId = this.dataset.blockId;
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5;
    this.showDots = this.dataset.showDots === 'true';
    this.slidesCount = parseInt(this.dataset.slidesCount, 10) || 0;
    
    // DOM elements
    this.swiperContainer = null;
    this.swiperWrapper = null;
    this.paginationEl = null;
    
    // Bind methods
    this.initSwiper = this.initSwiper.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    this.handleSlideChange = this.handleSlideChange.bind(this);
  }

  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    // Get DOM elements
    this.swiperContainer = this.querySelector('.jkl-promotional-banner__swiper');
    this.swiperWrapper = this.querySelector('.swiper-wrapper');
    this.paginationEl = this.querySelector('.jkl-promotional-banner__pagination');
    
    if (!this.swiperContainer || !this.swiperWrapper) return;
    
    // Only initialize Swiper if we have slides
    if (this.slidesCount > 0) {
      this.initSwiper();
    }
    
    // Listen for Theme Editor events
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener('shopify:block:select', this.handleSectionLoad);
  }

  initSwiper() {
    // Destroy existing instance if any
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    // Count visible slides
    const visibleSlides = this.swiperWrapper.querySelectorAll('.swiper-slide');
    const visibleCount = visibleSlides.length;
    
    if (visibleCount === 0) return;
    
    // Build Swiper configuration
    const swiperConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 500,
      allowTouchMove: visibleCount > 1,
      
      // Enable loop for seamless autoplay (matches Hydrogen PromotionalBanner)
      loop: visibleCount > 1,
      
      // Autoplay configuration
      autoplay: this.autoplay && visibleCount > 1 ? {
        delay: this.autoplaySpeed * 1000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      } : false,
      
      // Pagination dots
      pagination: this.showDots && visibleCount > 1 ? {
        el: this.paginationEl,
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      } : false,
      
      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
      },
      
      // Observer for dynamic content
      observer: true,
      observeParents: true,
      
      // Events
      on: {
        slideChange: this.handleSlideChange,
      },
    };
    
    // Initialize Swiper
    this.swiper = new Swiper(this.swiperContainer, swiperConfig);
    
    // Setup video handling
    this.setupVideoHandlers();
    
    // Log in design mode for debugging
    if (window.Shopify && window.Shopify.designMode) {
      console.log('[JKL Promotional Banner] Initialized', {
        slidesCount: this.slidesCount,
        visibleCount: visibleCount,
        autoplay: this.autoplay,
        loop: visibleCount > 1,
      });
    }
  }

  /**
   * Handle slide change event
   * Pause/play videos as needed
   */
  handleSlideChange() {
    if (!this.swiper) return;
    
    // Pause all videos
    const allVideos = this.querySelectorAll('.jkl-promo-slide__video');
    allVideos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });
    
    // Play video in active slide
    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    if (activeSlide) {
      const activeVideos = activeSlide.querySelectorAll('.jkl-promo-slide__video');
      activeVideos.forEach(video => {
        video.play().catch(() => {
          // Autoplay might be blocked, that's okay
        });
      });
    }
  }

  /**
   * Setup video event handlers
   * Auto-advance when video ends (if not looping)
   */
  setupVideoHandlers() {
    const videos = this.querySelectorAll('.jkl-promo-slide__video');
    
    videos.forEach(video => {
      // When video ends, advance to next slide
      video.addEventListener('ended', () => {
        if (this.swiper && !video.loop) {
          this.swiper.slideNext();
        }
      });
      
      // Pause autoplay while video is playing
      video.addEventListener('play', () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.stop();
        }
      });
      
      // Resume autoplay when video ends or is paused
      video.addEventListener('pause', () => {
        if (this.swiper && this.swiper.autoplay && this.autoplay) {
          this.swiper.autoplay.start();
        }
      });
    });
  }

  /**
   * Handle Theme Editor section/block load event
   */
  handleSectionLoad(event) {
    // Reinitialize after a short delay to ensure DOM is updated
    setTimeout(() => {
      this.init();
    }, 100);
  }

  /**
   * Cleanup when element is removed
   */
  disconnectedCallback() {
    // Destroy Swiper instance
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    // Remove event listeners
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    document.removeEventListener('shopify:block:select', this.handleSectionLoad);
  }
}

// Register custom element
if (!customElements.get('jkl-promotional-banner')) {
  customElements.define('jkl-promotional-banner', JklPromotionalBanner);
}
